import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { usePlanLimits } from '@features/subscription/hooks/usePlanLimits';
import { Clock, Lock } from 'lucide-react';

export interface IntervalOption {
  value: string;
  minutes: number;
}

// Extended options matching Schedules.tsx INTERVAL_OPTIONS
export const INTERVAL_OPTIONS: IntervalOption[] = [
  { value: '1m', minutes: 1 },
  { value: '3m', minutes: 3 },
  { value: '5m', minutes: 5 },
  { value: '10m', minutes: 10 },
  { value: '15m', minutes: 15 },
  { value: '30m', minutes: 30 },
  { value: '1h', minutes: 60 },
  { value: '2h', minutes: 120 },
  { value: '3h', minutes: 180 },
  { value: '4h', minutes: 240 },
  { value: '6h', minutes: 360 },
  { value: '8h', minutes: 480 },
  { value: '12h', minutes: 720 },
  { value: '24h', minutes: 1440 },
];

interface ScheduleIntervalSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  label?: string;
  error?: string;
  id?: string;
  showUpgradeLink?: boolean;
}

/**
 * Schedule interval dropdown with plan limit enforcement
 * Shows disabled options for intervals below plan minimum with upgrade tooltip
 *
 * @example
 * ```tsx
 * <ScheduleIntervalSelect
 *   value="6h"
 *   onChange={(value) => setIntervalValue(value)}
 *   label="Backup Interval"
 * />
 * ```
 */
export function ScheduleIntervalSelect({
  value,
  onChange,
  disabled = false,
  className = '',
  label = 'Interval',
  error,
  id = 'interval-select',
  showUpgradeLink = true,
}: ScheduleIntervalSelectProps) {
  const { planLimits, isLoading } = usePlanLimits();
  const { t } = useTranslation('backups');

  const minScheduleInterval = planLimits?.minScheduleInterval ?? 60;

  const options = useMemo(() => {
    return INTERVAL_OPTIONS.map((option) => ({
      ...option,
      isDisabled: option.minutes < minScheduleInterval,
    }));
  }, [minScheduleInterval]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    const selectedOption = options.find((o) => o.value === selectedValue);

    // Only allow selection if not disabled
    if (selectedOption && !selectedOption.isDisabled) {
      onChange(selectedValue);
    }
  };

  // Check if current selection is a disabled option
  const selectedOption = options.find((o) => o.value === value);
  const isCurrentSelectionDisabled = selectedOption?.isDisabled ?? false;

  return (
    <div className={`relative ${className}`}>
      <label
        htmlFor={id}
        className="block text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3 ml-1"
      >
        {label}
      </label>

      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={handleChange}
          disabled={disabled || isLoading}
          className={`
            w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark
            border border-border-strong-light dark:border-border-strong-dark
            rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none
            appearance-none cursor-pointer
            ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''}
            ${error || isCurrentSelectionDisabled ? 'border-state-danger-light dark:border-state-danger-dark focus:ring-state-danger-light dark:focus:ring-state-danger-dark' : ''}
          `}
        >
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.isDisabled}
              className={
                option.isDisabled
                  ? 'text-text-subtle-light dark:text-text-subtle-dark bg-surface-2-light dark:bg-surface-2-dark'
                  : ''
              }
            >
              {t('schedules.intervalSelect.every', {
                interval: t(
                  `schedules.intervalSelect.units.${option.value.endsWith('m') ? 'minute' : 'hour'}`,
                  {
                    count: Number.parseInt(option.value, 10),
                  }
                ),
              })}
              {option.isDisabled ? ` · ${t('schedules.intervalSelect.upgradeRequiredShort')}` : ''}
            </option>
          ))}
        </select>

        {/* Custom dropdown arrow */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg
            className="w-5 h-5 text-text-subtle-light dark:text-text-subtle-dark"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute right-10 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600" />
          </div>
        )}
      </div>

      {/* Error message for disabled selection */}
      {isCurrentSelectionDisabled && (
        <div className="mt-2 p-2 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <Lock
              size={14}
              className="mt-0.5 shrink-0 text-state-warn-light dark:text-state-warn-dark"
            />
            <div className="text-xs">
              <p className="font-bold text-state-warn-light dark:text-state-warn-dark">
                {t('schedules.intervalSelect.requiresUpgrade')}
              </p>
              <p className="text-state-warn-light dark:text-state-warn-dark mt-0.5">
                {t('schedules.intervalSelect.minimumInterval', { minutes: minScheduleInterval })}
              </p>
              {showUpgradeLink && (
                <Link
                  to="/billing"
                  className="mt-1 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-bold underline"
                >
                  {t('schedules.intervalSelect.viewPlans')} →
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generic error message */}
      {error && !isCurrentSelectionDisabled && (
        <p className="mt-2 text-xs font-bold text-state-danger-light dark:text-state-danger-dark ml-1">
          {error}
        </p>
      )}

      {/* Plan limit info */}
      {planLimits && !isLoading && (
        <div className="mt-2 flex items-center text-[10px] text-text-subtle-light dark:text-text-subtle-dark font-medium">
          <Clock size={12} className="mr-1" />
          {t('schedules.intervalSelect.planMinimum', { minutes: minScheduleInterval })}
        </div>
      )}
    </div>
  );
}

export default ScheduleIntervalSelect;
